// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
/// <summary>
/// Настройки спреда, для конкретного инструмента
import { MathUtils } from '@shared/utils/MathUtils';
import { ErrorInformationStorage } from '../ErrorInformationStorage';
import { SpreadMeasureMode, SpreadPlan } from './SpreadPlan';
import { ticksUtils } from '@shared/utils/Trading/TicksUtils';
/// </summary>
export class SpreadItem {
    CalcBid(bid, ask, instrument, round = true, defaultPointSize = 1) {
        try {
            // #32479  (Watchlist) некорректные даные в watchlist при использовании спред
            if (isNaN(bid) && isNaN(ask)) {
                return Number.NaN;
            }
            if (isNaN(bid) &&
                (this.SpreadMode === SpreadPlan.DYNAMIC_SPREADMODE ||
                    this.SpreadMode === SpreadPlan.BID_SPREADMODE ||
                    this.SpreadMode === SpreadPlan.NOT_FIXED_SPREADMODE)) // #45307
             {
                return Number.NaN;
            }
            // if (bid == 0 && ask == 0)
            //     return 0;
            let bidSpread;
            const instr = instrument !== null && instrument !== void 0 ? instrument : null;
            const useVariableTickSize = this.SpreadMeasure === SpreadMeasureMode.SPREAD_IN_PIPS &&
                !isNullOrUndefined(instr) &&
                instr.VariableTickList.length > 1;
            const SpreadCoef = this.getSpreadCoef(instr, defaultPointSize);
            switch (this.SpreadMode) {
                case SpreadPlan.ASK_SPREADMODE:
                    if (useVariableTickSize) {
                        bidSpread = !isNaN(ask) ? instr.calculatePrice(ask, this.AskShift - this.Spread) : bid;
                    }
                    else {
                        bidSpread = !isNaN(ask) ? ask + this.AskShift * SpreadCoef - this.Spread * SpreadCoef : bid;
                    }
                    break;
                case SpreadPlan.BID_ASK_SPREADMODE:
                    if (isNaN(ask)) {
                        ask = bid;
                    }
                    if (isNaN(bid)) {
                        bid = ask;
                    }
                    if (useVariableTickSize) {
                        const askWithAskShift = instr.calculatePrice(ask, this.AskShift);
                        const bidWithBidShift = instr.calculatePrice(bid, this.BidShift);
                        bidSpread = instr.calculatePrice((askWithAskShift + bidWithBidShift) / 2, -this.Spread / 2); // Spread здесь всегда четный
                    }
                    else {
                        bidSpread =
                            (ask +
                                this.AskShift * SpreadCoef +
                                bid +
                                this.BidShift * SpreadCoef -
                                this.Spread * SpreadCoef) /
                                2;
                    }
                    break;
                case SpreadPlan.LIMEN_SPREADMODE:
                    if (isNaN(ask)) {
                        ask = bid;
                    }
                    if (isNaN(bid)) {
                        bid = ask;
                    }
                    let newAsk, newBid, delta, resBid;
                    if (useVariableTickSize) {
                        newAsk = instr.calculatePrice(ask, this.AskShift);
                        newBid = instr.calculatePrice(bid, this.BidShift);
                        const delta_ticks = instr.calculateTicks(newBid, newAsk - newBid); // считаем в тиках
                        if (delta_ticks < this.Spread) {
                            resBid = instr.calculatePrice(newBid, (delta_ticks - this.Spread) / 2);
                        }
                        else {
                            resBid = newBid;
                        }
                    }
                    else {
                        newAsk = ask + this.AskShift * SpreadCoef;
                        newBid = bid + this.BidShift * SpreadCoef;
                        delta = newAsk - newBid - this.Spread * SpreadCoef;
                        resBid = newBid;
                        // if (delta < 0) neative
                        resBid = newBid + delta / 2;
                    }
                    bidSpread = resBid;
                    break;
                case SpreadPlan.DYNAMIC_SPREADMODE: // вымышленный тип под инструмент в пунктах
                    if (useVariableTickSize) {
                        const price = instr.calculatePrice(bid, bid * this.BidShift);
                        bidSpread = !isNaN(bid)
                            ? MathUtils.RoundToIncrement(price, ticksUtils.FindVariableTick(price, instr.VariableTickList).PointSize)
                            : 0;
                    }
                    else {
                        const price = bid - bid * this.BidShift * SpreadCoef;
                        const roundToValue = isNullOrUndefined(instr)
                            ? defaultPointSize
                            : ticksUtils.FindVariableTick(price, instr.VariableTickList).PointSize;
                        bidSpread = !isNaN(bid) ? MathUtils.RoundToIncrement(price, roundToValue) : 0;
                    }
                    break;
                case SpreadPlan.BID_SPREADMODE:
                case SpreadPlan.NOT_FIXED_SPREADMODE:
                default:
                    if (useVariableTickSize) {
                        bidSpread = !isNaN(bid) ? instr.calculatePrice(bid, this.BidShift) : bid;
                    }
                    else {
                        bidSpread = !isNaN(bid) ? bid + this.BidShift * SpreadCoef : bid;
                    }
                    break;
            }
            // negative
            // if (bidSpread < 0)
            //     return Number.NaN;
            // else
            return instr != null && round ? instr.roundPrice(bidSpread) : bidSpread;
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        return bid;
    }
    CalcAsk(bid, ask, instrument, round = true, defaultPointSize = 1) {
        try {
            // #32479  (Watchlist) некорректные даные в watchlist при использовании спред
            if (isNaN(bid) && isNaN(ask)) {
                return Number.NaN;
            }
            if (isNaN(ask) &&
                (this.SpreadMode === SpreadPlan.DYNAMIC_SPREADMODE ||
                    this.SpreadMode === SpreadPlan.ASK_SPREADMODE ||
                    this.SpreadMode === SpreadPlan.NOT_FIXED_SPREADMODE)) // #45307
             {
                return Number.NaN;
            }
            // if (bid == 0 && ask == 0)
            // return 0;
            let askSpread;
            const instr = instrument !== null && instrument !== void 0 ? instrument : null;
            const useVariableTickSize = this.SpreadMeasure === SpreadMeasureMode.SPREAD_IN_PIPS &&
                !isNullOrUndefined(instr) &&
                instr.VariableTickList.length > 1;
            const SpreadCoef = this.getSpreadCoef(instr, defaultPointSize);
            switch (this.SpreadMode) {
                case SpreadPlan.BID_SPREADMODE:
                    if (useVariableTickSize) {
                        askSpread = !isNaN(bid) ? instr.calculatePrice(bid, this.BidShift + this.Spread) : ask;
                    }
                    else {
                        askSpread = !isNaN(bid) ? bid + this.BidShift * SpreadCoef + this.Spread * SpreadCoef : ask;
                    }
                    break;
                case SpreadPlan.BID_ASK_SPREADMODE:
                    if (isNaN(bid)) {
                        bid = ask;
                    }
                    if (isNaN(ask)) {
                        ask = bid;
                    }
                    if (useVariableTickSize) {
                        const askWithAskShift = instr.calculatePrice(ask, this.AskShift);
                        const bidWithBidShift = instr.calculatePrice(bid, this.BidShift);
                        askSpread = instr.calculatePrice((askWithAskShift + bidWithBidShift) / 2, this.Spread / 2); // Spread здесь всегда четный
                    }
                    else {
                        askSpread =
                            (ask +
                                this.AskShift * SpreadCoef +
                                bid +
                                this.BidShift * SpreadCoef +
                                this.Spread * SpreadCoef) /
                                2;
                    }
                    break;
                case SpreadPlan.LIMEN_SPREADMODE:
                    if (isNaN(bid)) {
                        bid = ask;
                    }
                    if (isNaN(ask)) {
                        ask = bid;
                    }
                    let newAsk, newBid, delta, resAsk;
                    if (useVariableTickSize) {
                        newAsk = instr.calculatePrice(ask, this.AskShift);
                        newBid = instr.calculatePrice(bid, this.BidShift);
                        const delta_ticks = instr.calculateTicks(newBid, newAsk - newBid); // считаем в тиках
                        if (delta_ticks < this.Spread) {
                            resAsk = instr.calculatePrice(newAsk, -(delta_ticks - this.Spread) / 2);
                        }
                        else {
                            resAsk = newAsk;
                        }
                    }
                    else {
                        newAsk = ask + this.AskShift * SpreadCoef;
                        newBid = bid + this.BidShift * SpreadCoef;
                        delta = newAsk - newBid - this.Spread * SpreadCoef;
                        resAsk = newAsk;
                        // if (delta < 0) negative
                        resAsk = newAsk - delta / 2;
                    }
                    askSpread = resAsk;
                    break;
                case SpreadPlan.DYNAMIC_SPREADMODE: // вымышленный тип под инструмент в пунктах
                    if (useVariableTickSize) {
                        const price = instr.calculatePrice(ask, ask * this.AskShift);
                        askSpread = !isNaN(ask)
                            ? MathUtils.RoundToIncrement(price, ticksUtils.FindVariableTick(price, instr.VariableTickList).PointSize)
                            : 0;
                    }
                    else {
                        const price = ask + ask * this.AskShift * SpreadCoef;
                        const roundToValue = isNullOrUndefined(instr)
                            ? defaultPointSize
                            : ticksUtils.FindVariableTick(price, instr.VariableTickList).PointSize;
                        askSpread = !isNaN(ask) ? MathUtils.RoundToIncrement(price, roundToValue) : 0;
                    }
                    break;
                case SpreadPlan.ASK_SPREADMODE:
                case SpreadPlan.NOT_FIXED_SPREADMODE:
                default:
                    if (useVariableTickSize) {
                        askSpread = !isNaN(ask) ? instr.calculatePrice(ask, this.AskShift) : ask;
                    }
                    else {
                        askSpread = !isNaN(ask) ? ask + this.AskShift * SpreadCoef : ask;
                    }
                    break;
            }
            // negative
            // if (askSpread < 0)
            //     return Number.NaN;
            // else
            return instr != null && round ? instr.roundPrice(askSpread) : askSpread;
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        return ask;
    }
    getSpreadCoef(inst, defaultPointSize = 1) {
        switch (this.SpreadMeasure) {
            case SpreadMeasureMode.SPREAD_IN_PIPS:
                return inst ? inst.PointSize : defaultPointSize;
            case SpreadMeasureMode.SPREAD_IN_BASIC_POINTS:
            case SpreadMeasureMode.SPREAD_IN_BASIC_POINTS_POST_EXECUTION:
                return 0.0001;
            case SpreadMeasureMode.SPREAD_IN_CURRENCY:
            case SpreadMeasureMode.SPREAD_IN_CURRENCY_POST_EXECUTION:
            default:
                return defaultPointSize;
        }
    }
}
